import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import noop from 'lib/function/noop'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { DesktopLayoutSizes } from 'tripPlanner/components/ItemCard/Common/utils'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'

const Card = styled(ClickableLink)`
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  overflow: hidden;
  border-radius: ${(props) => props.theme.borderRadius.M};
  min-height: ${rem(75)};
  width: 100%;
  break-inside: avoid-page;

  ${cardItemHoverAnimation}

  ${DesktopLayoutSizes} {
    flex-direction: row;
  }

  @media print {
    flex-direction: row;
    text-decoration: none;
  }
`

interface Props {
  children: React.ReactNode
  to?: string
  tripId: string
  sectionId: string
  isSoldOut?: boolean
  openIn: 'newWindow' | 'immersiveMode'
  testId?: string
  nonInteractable?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

function BaseSavedOffer({
  children,
  to,
  tripId,
  isSoldOut,
  openIn,
  sectionId,
  testId,
  nonInteractable = false,
  onClick = noop,
}: Props) {
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!e.isPropagationStopped()) {
        if (openIn === 'immersiveMode' && !isSoldOut) {
          setImmersiveMode({ tripId, sectionId })
        }
        onClick(e)
      }
    },
    [openIn, isSoldOut, onClick, setImmersiveMode, tripId, sectionId],
  )

  const source = useTripClientSource()

  const testIds = cn('trip-base-item-card', testId)

  if (nonInteractable) {
    return (
      <Card as="div" data-testid={testIds}>
        {children}
      </Card>
    )
  }

  const enrichedTo = to && addQueryParamsToPath(to, { source })

  return (
    <Card
      data-testid={testIds}
      to={enrichedTo}
      target={openIn === 'newWindow' ? '_blank' : undefined}
      onClick={handleOnClick}
    >
      {children}
    </Card>
  )
}

export default BaseSavedOffer
